"use client";

import { useEffect, useState } from "react";

import type { AdminSession } from "@/lib/admin";

import AdminBarClient, { type AdminBarNote } from "./AdminBarClient";

type AdminContext = { admin: AdminSession | null; notes: AdminBarNote[] };

/**
 * Renders nothing for visitors. The state is fetched at runtime rather than
 * resolved on the server so that /notes and each note page stay statically
 * cached — reading the session cookie during render would force every one of
 * them to be server-rendered on demand.
 */
export default function AdminBar() {
  const [context, setContext] = useState<AdminContext | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/admin/context", { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: AdminContext | null) => {
        if (data?.admin) setContext(data);
      })
      .catch(() => {
        /* not signed in, offline, or the route is unavailable — show nothing */
      });

    return () => controller.abort();
  }, []);

  if (!context?.admin) return null;

  return <AdminBarClient admin={context.admin} notes={context.notes} />;
}
